import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Skeleton,
  Stack,
  Badge,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Emppersonal from './EmpDetails/Emppersonal';
import { AuthContext } from '../../context/logeduser';
import { useGetSingleEmployee, useUpdatePhoto } from '../../hooks/useEmployees';
import EmpAccount from './EmpDetails/EmpAccount';
import EmpWrkDetails from './EmpDetails/EmpWrkDetails';
import EmpPhoto from './EmpDetails/EmpPhoto';
import EmpDcs from './EmpDcs';
import EmpAssets from './EmpDetails/EmpAssets';
import Payslipemp from './Payslipemp';
import { getMediaUrl } from '../../utils/urlHelper';
import { getInitials } from '../../utils/initials';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EditIcon from '@mui/icons-material/Edit';
import WorkIcon from '@mui/icons-material/Work';
import DescriptionIcon from '@mui/icons-material/Description';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VerifiedIcon from '@mui/icons-material/Verified';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TuneIcon from '@mui/icons-material/Tune';
import CustomFieldsSection, {
  useCustomFieldDefinitions,
} from '../employees/CustomFieldsSection';

// ---- Visual tokens (shared with the employee Home, Attendance & Payslip dashboards) ----
// Same soft FORTHEYE-style cards used on /home so the portal feels like one app.
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';   // primary blue, matches /home
const MONEY = '#16a34a';    // green for the CTC / payslip pill, matches /home

// Navigation items configuration
const navigationItems = [
  { id: 'EmpPersonal', label: 'Personal', icon: <PersonIcon /> },
  { id: 'bank', label: 'Account', icon: <AccountBalanceIcon /> },
  { id: 'services', label: 'Edit', icon: <EditIcon /> },
  { id: 'work', label: 'Employment', icon: <WorkIcon /> },
  { id: 'custom', label: 'Additional', icon: <TuneIcon /> },
  { id: 'Docs', label: 'Documents', icon: <DescriptionIcon /> },
  { id: 'assets', label: 'Assets', icon: <InventoryIcon /> },
  { id: 'payslips', label: 'Payslips', icon: <ReceiptIcon /> },
];

// Soft info pill used on the right of the header strip (mirrors /home's top bar).
const HeaderPill = ({ icon, label, value, onClick, money }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      px: 1.75, py: 1, borderRadius: '14px',
      bgcolor: money ? '#f0fdf4' : '#f8fafc',
      border: `1px solid ${money ? '#dcfce7' : '#eef0f4'}`,
      cursor: onClick ? 'pointer' : 'default',
      minWidth: 0,
    }}
  >
    {React.cloneElement(icon, { sx: { color: money ? MONEY : '#6b7280', fontSize: 18 } })}
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', lineHeight: 1, fontSize: '0.62rem' }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.85rem', lineHeight: 1.2 }} noWrap>
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

const ProfileEmp = () => {
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState('EmpPersonal');
  const [loading, setLoading] = useState(true);
  const [photoPreview, setPhotoPreview] = useState(null);

  const { authData } = useContext(AuthContext);
  const { data: singleEmployee } = useGetSingleEmployee({ id: authData?.employee_no });
  const updatePhoto = useUpdatePhoto();
  const customFields = useCustomFieldDefinitions();

  const emp = singleEmployee?.employee || {};

  useEffect(() => {
    if (singleEmployee) {
      setLoading(false);
    }
  }, [singleEmployee]);

  const handlePhotoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    updatePhoto.mutate({ employee_no: emp?.employee_no || authData?.employee_no, file });
  };

  // Function to render the active component
  const renderSection = () => {
    const sections = {
      'EmpPersonal': <Emppersonal data={singleEmployee?.employee} />,
      'bank': <EmpAccount data={singleEmployee?.employee} />,
      'services': <EmpPhoto data={singleEmployee?.employee} />,
      'work': <EmpWrkDetails data={singleEmployee?.employee} />,
      'custom': (
        <CustomFieldsSection
          definitions={customFields.definitions}
          loading={customFields.loading}
          error={customFields.error}
          values={singleEmployee?.employee?.custom_attributes || {}}
          readOnly
          title="Additional Employee Details"
        />
      ),
      'Docs': <EmpDcs data={singleEmployee?.documents} />,
      'assets': <EmpAssets data={singleEmployee?.assets} />,
      'payslips': <Payslipemp />,
    };
    return sections[activeSection] || <Emppersonal data={singleEmployee?.employee} />;
  };

  // Loading state
  if (loading && !singleEmployee) {
    return (
      <Box sx={{ pb: { xs: 4, md: 2 } }}>
        <Skeleton variant="rounded" height={130} sx={{ mb: { xs: 2, md: 3 }, borderRadius: '22px' }} />
        <Skeleton variant="rounded" height={440} sx={{ borderRadius: '22px' }} />
      </Box>
    );
  }

  const status = emp?.status || emp?.employment_status;
  const isActive = String(status).toLowerCase() === 'active' || String(status).toLowerCase() === 'confirmed';

  return (
    <Box sx={{ width: '100%', minWidth: 0, pb: { xs: 4, md: 2 } }}>
      {/* ---- Header strip (FORTHEYE soft card, same language as /home) ---- */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 2, md: 3 } }}>
          {/* Identity */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 1.75, md: 2.5 }, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={<Box sx={{ width: 15, height: 15, borderRadius: '50%', bgcolor: '#22c55e', border: '2.5px solid #fff' }} />}
            >
              <Box sx={{ position: 'relative', '&:hover .photo-overlay': { opacity: 1 } }}>
                <Avatar
                  src={photoPreview || getMediaUrl(emp?.photo)}
                  sx={{
                    width: { xs: 68, sm: 84, md: 100 }, height: { xs: 68, sm: 84, md: 100 },
                    bgcolor: `${ACCENT}14`, color: ACCENT, fontWeight: 700, fontSize: { xs: '2rem', md: '2.4rem' },
                    border: '3px solid #fff', boxShadow: '0 8px 20px rgba(17,24,39,0.10)',
                  }}
                >
                  {getInitials(emp?.name)}
                </Avatar>
                <Box
                  component="label"
                  className="photo-overlay"
                  sx={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    bgcolor: 'rgba(15,23,42,0.45)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: 0, transition: 'opacity 0.25s ease', cursor: 'pointer', backdropFilter: 'blur(1px)',
                  }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 26 }} />
                  <input type="file" hidden accept="image/*" onChange={handlePhotoUpload} />
                </Box>
              </Box>
            </Badge>

            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, color: '#111827', lineHeight: 1.15, letterSpacing: '-0.02em', overflowWrap: 'anywhere' }}>
                {emp?.name || 'Employee'}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  icon={<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#8b5cf6', ml: '8px !important' }} />}
                  label={emp?.designation || 'Designation'}
                  sx={{ height: 26, fontWeight: 600, fontSize: '0.74rem', bgcolor: '#f5f3ff', color: '#6d28d9', '& .MuiChip-icon': { mr: '-2px' } }}
                />
                <Chip
                  size="small"
                  icon={<BadgeIcon sx={{ fontSize: '15px !important', color: `${ACCENT} !important` }} />}
                  label={emp?.employee_no || 'EMP-000'}
                  sx={{ height: 26, fontWeight: 700, fontSize: '0.74rem', bgcolor: '#eef4ff', color: ACCENT }}
                />
                {status && (
                  <Chip
                    size="small"
                    icon={<VerifiedIcon sx={{ fontSize: '15px !important', color: `${isActive ? MONEY : '#94a3b8'} !important` }} />}
                    label={String(status).charAt(0).toUpperCase() + String(status).slice(1)}
                    sx={{ height: 26, fontWeight: 600, fontSize: '0.74rem', textTransform: 'capitalize', bgcolor: isActive ? '#f0fdf4' : '#f1f5f9', color: isActive ? MONEY : '#64748b' }}
                  />
                )}
              </Stack>
            </Box>
          </Box>

          {/* Right info pills */}
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ ml: { md: 'auto' }, width: { xs: '100%', sm: 'auto' } }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <HeaderPill icon={<BusinessIcon />} label="Department" value={emp?.department} />
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <HeaderPill icon={<CalendarMonthIcon />} label="Joined" value={emp?.date_of_joining} />
            </Box>
            <HeaderPill icon={<AccountBalanceWalletIcon />} label="Payslips" value="View" money onClick={() => navigate('/payslip')} />
          </Stack>
        </Box>
      </Paper>

      {/* ---- Content card: pill tab bar + active section ---- */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex', gap: 1, px: { xs: 1.25, sm: 2 }, pt: 1.5, pb: 1.5,
            overflowX: 'auto',
            position: 'sticky', top: 0, zIndex: 5, bgcolor: '#fff',
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#e5e7eb', borderRadius: 3 },
          }}
        >
          {navigationItems.map((item) => {
            const active = activeSection === item.id;
            return (
              <Box
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: { xs: 1.5, sm: 2 }, py: 1.1, borderRadius: '12px',
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                  fontWeight: 600, fontSize: { xs: '0.82rem', sm: '0.9rem' },
                  color: active ? ACCENT : '#6b7280',
                  bgcolor: active ? '#eef4ff' : 'transparent',
                  border: `1px solid ${active ? '#dbe4ff' : 'transparent'}`,
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: active ? '#eef4ff' : '#f8fafc', color: active ? ACCENT : '#374151' },
                }}
              >
                {React.cloneElement(item.icon, { sx: { fontSize: 18 } })}
                {item.label}
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ borderColor: '#eef0f4' }} />

        <Box
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            animation: 'profileFade 0.35s ease-in-out',
            '@keyframes profileFade': {
              '0%': { opacity: 0, transform: 'translateY(8px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          {renderSection()}
        </Box>
      </Paper>
    </Box>
  );
};

export default ProfileEmp;
